import { useEffect, useState } from "react";
import { db } from "./firebase";
import { doc, updateDoc, collection, addDoc } from "firebase/firestore";
import { inputStyle, btnStyle, btnSecondaryStyle, cardStyle, labelStyle, colors } from "./lib/theme";
import { nextSocialRound } from "./lib/roundRobin";

export default function SocialSession({ sessionId, live }) {
  var players = live.players || [];
  var names = live.playerNames || {};
  var round = live.currentRound;

  var [gameType, setGameType] = useState(live.gameType || "score");
  var [targetScore, setTargetScore] = useState(live.targetScore || 11);
  var [timerMinutes, setTimerMinutes] = useState(live.timerMinutes || 15);
  var [scores, setScores] = useState({}); // court -> [scoreA, scoreB]
  var [secondsLeft, setSecondsLeft] = useState(null);

  useEffect(function () {
    if (gameType !== "timer" || !round || round.startedAt == null) { setSecondsLeft(null); return; }
    var totalSeconds = (live.timerMinutes || timerMinutes) * 60;
    var tick = function () {
      var elapsed = Math.floor((Date.now() - round.startedAt) / 1000);
      setSecondsLeft(Math.max(0, totalSeconds - elapsed));
    };
    tick();
    var id = setInterval(tick, 1000);
    return function () { clearInterval(id); };
  }, [round, gameType, timerMinutes, live.timerMinutes]);

  function name(uid) { return names[uid] || uid; }
  function teamName(team) { return team.map(name).join(" & "); }

  function startRound() {
    var pairing = nextSocialRound(players, live.courtCount, live.pairingStats);
    var courts = pairing.courts.map(function (c) {
      return { court: c.court, teamA: c.teamA, teamB: c.teamB, completed: false };
    });
    updateDoc(doc(db, "liveSessions", sessionId), {
      gameType: gameType,
      targetScore: parseInt(targetScore, 10),
      timerMinutes: parseInt(timerMinutes, 10),
      round: (live.round || 0) + 1,
      currentRound: {
        roundNumber: (live.round || 0) + 1,
        courts: courts,
        sitOut: pairing.sitOut,
        startedAt: Date.now(),
      },
      pairingStats: pairing.stats,
    });
    setScores({});
  }

  function submitCourtScore(court) {
    var s = scores[court.court] || [0, 0];
    var sA = parseInt(s[0], 10) || 0;
    var sB = parseInt(s[1], 10) || 0;
    var winningTeam = sA === sB ? null : (sA > sB ? "A" : "B");

    addDoc(collection(db, "matches"), {
      sessionId: sessionId,
      templateId: live.templateId,
      occurrenceDate: live.occurrenceDate,
      venueId: live.venueId,
      venueName: live.venueName,
      mode: "social",
      court: court.court,
      teamA: court.teamA,
      teamB: court.teamB,
      players: court.teamA.concat(court.teamB),
      scoreA: sA,
      scoreB: sB,
      winningTeam: winningTeam,
      timestamp: new Date().toISOString(),
    });

    var updatedCourts = round.courts.map(function (c) {
      return c.court === court.court ? { ...c, completed: true, scoreA: sA, scoreB: sB } : c;
    });
    updateDoc(doc(db, "liveSessions", sessionId), {
      currentRound: { ...round, courts: updatedCourts },
    });
  }

  var allCompleted = round && round.courts.every(function (c) { return c.completed; });

  if (!round) {
    return (
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, fontSize: 15 }}>Start Social Play</h3>
        <div style={{ marginBottom: 14 }}>
          <div style={labelStyle}>Game Type</div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={function () { setGameType("score"); }} style={gameType === "score" ? btnStyle : btnSecondaryStyle}>Play to a Score</button>
            <button onClick={function () { setGameType("timer"); }} style={gameType === "timer" ? btnStyle : btnSecondaryStyle}>Timed</button>
          </div>
        </div>
        {gameType === "score" ? (
          <div style={{ marginBottom: 14 }}>
            <div style={labelStyle}>Points to Win</div>
            <input style={inputStyle} type="number" min="1" value={targetScore} onChange={function (e) { setTargetScore(e.target.value); }} />
          </div>
        ) : (
          <div style={{ marginBottom: 14 }}>
            <div style={labelStyle}>Minutes per Round</div>
            <input style={inputStyle} type="number" min="1" value={timerMinutes} onChange={function (e) { setTimerMinutes(e.target.value); }} />
          </div>
        )}
        <p style={{ fontSize: 12, color: colors.textDim }}>
          {players.length + " players across " + live.courtCount + " court(s) — doubles, 4 per court"}
        </p>
        {players.length < 4 ? (
          <p style={{ fontSize: 13, color: colors.danger }}>Need at least 4 players for a doubles match.</p>
        ) : (
          <button style={btnStyle} onClick={function () { startRound(); }}>Start Round 1</button>
        )}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>{"Round " + round.roundNumber}</h3>
        {gameType === "timer" && secondsLeft !== null && (
          <div style={{ fontSize: 14, fontWeight: 700, color: secondsLeft === 0 ? colors.danger : colors.accent }}>
            {secondsLeft === 0 ? "Time's up!" : Math.floor(secondsLeft / 60) + ":" + String(secondsLeft % 60).padStart(2, "0")}
          </div>
        )}
      </div>

      {round.courts.map(function (c) {
        var s = scores[c.court] || ["", ""];
        return (
          <div key={c.court} style={{ ...cardStyle, marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: colors.textDim, marginBottom: 6 }}>{"Court " + c.court}</div>
            {c.completed ? (
              <div style={{ fontSize: 14 }}>
                {teamName(c.teamA)} {c.scoreA} — {c.scoreB} {teamName(c.teamB)}
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ flex: 1, fontSize: 14 }}>{teamName(c.teamA)}</div>
                <input style={{ ...inputStyle, width: 60 }} type="number" value={s[0]} onChange={function (e) {
                  setScores(function (prev) { return { ...prev, [c.court]: [e.target.value, s[1]] }; });
                }} />
                <span style={{ color: colors.textDim }}>vs</span>
                <input style={{ ...inputStyle, width: 60 }} type="number" value={s[1]} onChange={function (e) {
                  setScores(function (prev) { return { ...prev, [c.court]: [s[0], e.target.value] }; });
                }} />
                <div style={{ flex: 1, fontSize: 14, textAlign: "right" }}>{teamName(c.teamB)}</div>
              </div>
            )}
            {!c.completed && (
              <button style={{ ...btnSecondaryStyle, marginTop: 10 }} onClick={function () { submitCourtScore(c); }}>Submit Score</button>
            )}
          </div>
        );
      })}

      {round.sitOut.length > 0 && (
        <p style={{ fontSize: 12, color: colors.textDim }}>{"Sitting out this round: " + round.sitOut.map(name).join(", ")}</p>
      )}

      {allCompleted && (
        <button style={btnStyle} onClick={function () { startRound(); }}>{"Start Round " + (round.roundNumber + 1)}</button>
      )}
    </div>
  );
}
